import React, { useMemo, useState } from "react";
import _ from "lodash";

import { RBACAccessLevel, RBACDomain } from "@/access-control/types";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Button } from "@/design-system/components/Button/Button";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import { SaaSDestination } from "@/destinations/destinations";
import {
  getDefaultValuesFromConfigFields,
  getNonSensitiveSettings,
  makeDestSaveFunction,
  ModalField,
} from "@/destinations/modals/ConfigureIntegrationModal";
import useListAllDestinationsForEnv from "@/destinations/v2/hooks/useListAllDestinationsForEnv";
import { getDestinationConfigValidationErrors } from "@/destinations/validation";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import { useUserHasAccess } from "@/hooks/useUserHasAccess";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { getMissingRequiredFields, isFieldVisible } from "../configFields";
import { EnableDestinationStepProps } from "../stepProps";
import { getSetupPageReason } from "../wizardSupport";

function mapControlValues(
  destination: SaaSDestination,
  values: Record<string, unknown>,
): Record<string, unknown> {
  return _.mapValues(values, (value, key) => {
    const field = destination.configFields.find((f) => f.key === key);
    return field?.mapControlValueToConfigValue
      ? field.mapControlValueToConfigValue(value)
      : value;
  });
}

export function ConnectAccountStep({
  data,
  context,
  updateData,
  goNext,
}: EnableDestinationStepProps) {
  const {
    saasDestination: destination,
    configuredDestination,
    destinationsLoaded,
    isHipaaV2,
  } = context;
  const accountMetadata = useAccountMetadata();
  const canWrite = useUserHasAccess(
    RBACDomain.destinationConfiguration,
    RBACAccessLevel.write,
  );
  const { refetch: refetchDestinations } = useListAllDestinationsForEnv();
  const [saving, setSaving] = useState(false);

  const existingSettings = useMemo(
    () => configuredDestination?.settings ?? {},
    [configuredDestination],
  );
  const values = useMemo(() => {
    if (!destination) {
      return {};
    }
    return {
      ...getDefaultValuesFromConfigFields(destination.configFields),
      ...getNonSensitiveSettings(existingSettings, destination.configFields),
      ...data.credentialDraft,
    };
  }, [destination, existingSettings, data.credentialDraft]);

  if (!destination) {
    return (
      <WizardStepLayout
        title="Connect your account"
        primaryAction={{ label: "Continue", onClick: goNext, disabled: true }}
      >
        <Banner type="warning">Choose a destination first.</Banner>
      </WizardStepLayout>
    );
  }

  const setupPageReason = getSetupPageReason(destination, { isHipaaV2 });
  if (setupPageReason) {
    return (
      <WizardStepLayout
        title={`Connect ${destination.name}`}
        primaryAction={{
          label: "Continue",
          onClick: goNext,
          disabled: true,
          disabledReason: `Set up ${destination.name} on its page`,
        }}
      >
        <Banner type="warning">
          {setupPageReason}{" "}
          <WizardLink
            to={
              configuredDestination
                ? `${destination.configPageUrl()}/${configuredDestination.id}`
                : destination.configPageUrl()
            }
            className="cursor-pointer underline"
          >
            Open {destination.name} setup
          </WizardLink>
        </Banner>
      </WizardStepLayout>
    );
  }

  if (!destinationsLoaded) {
    return (
      <WizardStepLayout
        title={`Connect ${destination.name}`}
        primaryAction={{ label: "Continue", onClick: goNext, disabled: true }}
      >
        <LoadingSpinner className="size-6" />
      </WizardStepLayout>
    );
  }

  const hasUnsavedChanges = Object.keys(data.credentialDraft).length > 0;
  const isSaved = !!configuredDestination && !hasUnsavedChanges;
  const savedSecretKeys = new Set(
    destination.configFields
      .filter(
        (field) =>
          field.sensitive &&
          field.key in existingSettings &&
          !(field.key in data.credentialDraft),
      )
      .map((field) => field.key),
  );
  const validationErrors = getDestinationConfigValidationErrors(destination, {
    ...existingSettings,
    ...values,
  });
  const missingFields = getMissingRequiredFields(
    destination.configFields,
    { ...existingSettings, ...values },
    savedSecretKeys,
  );
  const blockingReason =
    canWrite === false
      ? "Ask an admin for permission to edit destinations"
      : missingFields.length > 0
        ? `Fill in ${missingFields.join(", ")} to continue`
        : Object.values(validationErrors)[0];

  function handleFieldChange(key: string, value: unknown) {
    updateData({ credentialDraft: { ...data.credentialDraft, [key]: value } });
  }

  async function advanceWithConfig(id: string, savedCredentials: boolean) {
    updateData({
      destinationConfigId: id,
      credentialDraft: {},
      ...(id !== configuredDestination?.id && { eventToggles: {} }),
      ...(savedCredentials && {
        credentialsSavedAt: Date.now(),
        liveViewSeenAt: undefined,
        deliveredAt: undefined,
        confirmedInDestination: undefined,
      }),
    });
    await refetchDestinations();
    goNext();
  }

  async function handleSave() {
    if (!destination) {
      return;
    }
    setSaving(true);
    if (isSaved && configuredDestination) {
      await advanceWithConfig(configuredDestination.id, false);
      return;
    }
    const saved: { id?: string } = {};
    const save = makeDestSaveFunction(
      destination,
      existingSettings,
      configuredDestination ?? {},
      mapControlValues(destination, values),
      (_dest: SaaSDestination, id: string) => {
        saved.id = id;
      },
      () => {},
      () => {},
      accountMetadata,
    );
    await save();
    if (!saved.id) {
      setSaving(false);
      return;
    }
    await advanceWithConfig(saved.id, true);
  }

  return (
    <WizardStepLayout
      title={`Connect ${destination.name}`}
      description={`Copy the credentials from your ${destination.name} account and paste them below.`}
      secondaryActions={
        hasUnsavedChanges && (
          <Button
            variant="ghost"
            onClick={() => updateData({ credentialDraft: {} })}
            disabled={saving}
          >
            Discard changes
          </Button>
        )
      }
      primaryAction={{
        label: isSaved ? "Continue" : "Save and continue",
        onClick: () => void handleSave(),
        loading: saving,
        disabled: !isSaved && !!blockingReason,
        disabledReason: blockingReason,
      }}
    >
      {destination.configHelp && (
        <Typography variant="body-sm" as="div" color="subtle">
          {destination.configHelp}
        </Typography>
      )}
      {canWrite === false && (
        <Banner type="warning">
          You don’t have permission to edit destinations. Ask an admin to
          connect {destination.name}.
        </Banner>
      )}
      {isSaved && (
        <Banner type="success">
          {destination.name} is connected. Edit a field to update it.
        </Banner>
      )}
      <div className="flex flex-col gap-2">
        {destination.configFields
          .filter((field) => isFieldVisible(field, values))
          .map((field) => (
            <ModalField
              key={field.key}
              field={field}
              values={values}
              settings={existingSettings}
              handleFieldChange={handleFieldChange}
              checkboxEnabled={canWrite !== false}
            />
          ))}
      </div>
    </WizardStepLayout>
  );
}
